import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Image } from "react-native";
import { useFragment } from "react-relay";

import { PersonFilledIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  Text,
  Box,
  HStack,
} from "@whoosh/design-system/src/components/primitive";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";

import { EventDetailDescription } from "../EventDetailDescription";
import { EventDetailHosts } from "../EventDetailHosts";
import { EventDetailPairings } from "../EventDetailPairings";
import { EventDetailSeriesInfo } from "../EventDetailSeriesInfo";
import { EventDetailInfo } from "./EventDetailInfo";
import { EventDetailMultiSessionEventDisplay } from "./EventDetailMultiSessionEventDisplay";
import { EventDetailBody$key } from "./__generated__/EventDetailBody.graphql";

type EventDetailBodyProps = {
  fragmentRef: EventDetailBody$key;
};

export const EventDetailBody = ({ fragmentRef }: EventDetailBodyProps) => {
  const data = useFragment(EventDetailBodyFragment, fragmentRef);
  const publicId = data.eventSeries.image?.publicId;

  const isMultiSessionEvent =
    data.eventSessions?.edges?.length && data.eventSessions?.edges?.length > 1;

  const isGroupCapacity = data.capacityType === "GROUPS";
  const { capacity, spotsAvailable } = data;

  const numAttendees =
    capacity &&
    spotsAvailable &&
    Number.isFinite(capacity) &&
    Number.isFinite(spotsAvailable)
      ? capacity - spotsAvailable
      : null;

  const attendeesInfo =
    !isMultiSessionEvent && !!numAttendees ? (
      <HStack gap={2} alignItems="center" testID="attendees-info">
        <PersonFilledIcon fill="black" color="icon-primary" />
        <Text
          maxFontSizeMultiplier={2}
          variant="mainSmallSemiBold"
          fontWeight={600}
        >
          {`${isGroupCapacity ? t`Groups` : t`Attendees`}: ${numAttendees}${
            capacity ? `/${capacity}` : ""
          }`}
        </Text>
      </HStack>
    ) : null;

  return (
    <VStack justifyContent="center" alignItems="center" mt={8}>
      {publicId ? (
        <Box
          width="100%"
          maxWidth={{ base: "100%", lg: 536 }}
          borderRadius="2"
          overflow="hidden"
        >
          <Image
            source={{
              // 16:9 aspect ratio
              uri: getThumbnail(publicId, 640, 360)?.toURL(),
            }}
            alt={t`Event cover image`}
            style={{ width: "100%", aspectRatio: 16 / 9 }}
          />
        </Box>
      ) : null}
      <VStack width="100%" pt={{ base: 6, lg: 8 }}>
        {isMultiSessionEvent ? (
          <EventDetailMultiSessionEventDisplay fragmentRef={data} />
        ) : (
          <EventDetailInfo
            attendeesInfo={attendeesInfo}
            fragmentRef={data.eventSessions?.edges?.[0]?.node ?? null}
          />
        )}
        <EventDetailPairings fragmentRef={data} />
        <EventDetailSeriesInfo fragmentRef={data} />
      </VStack>
      <EventDetailHosts
        fragmentRef={data.eventSessions?.edges?.[0]?.node ?? null}
      />
      {data.description ? (
        <EventDetailDescription description={data.description} />
      ) : null}
    </VStack>
  );
};

export const EventDetailBodyFragment = graphql`
  fragment EventDetailBody on Event {
    ...EventDetailAttendeesList
    ...EventDetailGroupDisplay_event
    ...EventDetailMultiSessionEventDisplay_event
    ...EventDetailSeriesInfo_event
    ...EventDetailPairings_event
    description
    spotsAvailable
    signUpStatusForClient(clientId: $memberId)
    eventSessions(first: 1000) {
      edges {
        node {
          ...EventDetailHosts
          ...EventDetailInfo_eventSession
        }
      }
    }
    capacityType
    maxGuestCount
    maxMemberCount
    minMemberCount
    minGuestCount
    capacity
    allowedGroupSizes
    eventSignUps: signUps(waitlist: false) {
      edges {
        node {
          id
          registrations {
            count
          }
          ...EventDetailAttendeesListItemFragment
          ...EventDetailAttendeesGroupListItemFragment
        }
      }
    }
    eventSeries {
      image {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
      color
      published
    }
  }
`;
